import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api, type TaskScope } from "./api";

const LIVE = { refetchInterval: 15_000, refetchOnWindowFocus: true } as const;

export const keys = {
  me: ["me"] as const,
  setup: ["setup"] as const,
  users: ["users"] as const,
  notifications: ["notifications"] as const,
  projects: ["projects"] as const,
  project: (pid: string) => ["p", pid] as const,
  bundle: (pid: string) => ["p", pid, "bundle"] as const,
  epics: (pid: string) => ["p", pid, "epics"] as const,
  epic: (pid: string, id: string) => ["p", pid, "epic", id] as const,
  sprints: (pid: string, epicId?: string) => ["p", pid, "sprints", epicId ?? "all"] as const,
  sprint: (pid: string, id: string) => ["p", pid, "sprint", id] as const,
  tasks: (pid: string, scope: TaskScope) => ["p", pid, "tasks", scope] as const,
  task: (pid: string, id: string) => ["p", pid, "task", id] as const,
  comments: (pid: string, entityId: string) => ["p", pid, "comments", entityId] as const,
  activity: (pid: string, entityId: string) => ["p", pid, "activity", entityId] as const,
  projectActivity: (pid: string) => ["p", pid, "project-activity"] as const,
  files: (pid: string, entityId?: string) => ["p", pid, "files", entityId ?? "all"] as const,
  finance: (pid: string) => ["p", pid, "finance"] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: api.me, retry: false, staleTime: 60_000 });
export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: api.users, staleTime: 30_000 });
export const useProjects = () => useQuery({ queryKey: keys.projects, queryFn: api.projects, ...LIVE, refetchInterval: 60_000 });
export const useNotifications = () =>
  useQuery({ queryKey: keys.notifications, queryFn: api.notifications, refetchInterval: 20_000, refetchOnWindowFocus: true });

export const useBundle = (pid: string) =>
  useQuery({ queryKey: keys.bundle(pid), queryFn: () => api.project(pid), refetchInterval: 30_000, retry: 1 });

export const useEpics = (pid: string) => useQuery({ queryKey: keys.epics(pid), queryFn: () => api.epics(pid), ...LIVE });
export const useEpic = (pid: string, id: string) => useQuery({ queryKey: keys.epic(pid, id), queryFn: () => api.epic(pid, id), ...LIVE });
export const useSprints = (pid: string, epicId?: string) =>
  useQuery({ queryKey: keys.sprints(pid, epicId), queryFn: () => api.sprints(pid, epicId), ...LIVE });
export const useSprint = (pid: string, id: string) =>
  useQuery({ queryKey: keys.sprint(pid, id), queryFn: () => api.sprint(pid, id), ...LIVE });
export const useTasks = (pid: string, scope: TaskScope, enabled = true) =>
  useQuery({ queryKey: keys.tasks(pid, scope), queryFn: () => api.tasks(pid, scope), enabled, ...LIVE });
export const useTask = (pid: string, id: string) => useQuery({ queryKey: keys.task(pid, id), queryFn: () => api.task(pid, id), ...LIVE });
export const useComments = (pid: string, entityId: string) =>
  useQuery({ queryKey: keys.comments(pid, entityId), queryFn: () => api.comments(pid, entityId), ...LIVE });
export const useActivity = (pid: string, entityId: string) =>
  useQuery({ queryKey: keys.activity(pid, entityId), queryFn: () => api.activity(pid, entityId), ...LIVE });
export const useProjectActivity = (pid: string) =>
  useQuery({ queryKey: keys.projectActivity(pid), queryFn: () => api.projectActivity(pid), ...LIVE, refetchInterval: 30_000 });
export const useFiles = (pid: string, entityId?: string) =>
  useQuery({ queryKey: keys.files(pid, entityId), queryFn: () => api.files(pid, entityId), ...LIVE, refetchInterval: 60_000 });

export const useFinance = (pid: string) =>
  useQuery({ queryKey: keys.finance(pid), queryFn: () => api.finance(pid), ...LIVE, refetchInterval: 30_000 });

/**
 * Mutation that refreshes everything under the given keys afterwards (defaults to the whole project),
 * and reports errors through the toast system.
 */
export function useAction<TVars = void, TData = unknown>(
  fn: (vars: TVars) => Promise<TData>,
  opts: { invalidate?: QueryKey[]; onSuccess?: (data: TData, vars: TVars) => void; onError?: (err: unknown) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: opts.onSuccess,
    onError: (err) => {
      if (opts.onError) opts.onError(err);
      else window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    },
    onSettled: () => Promise.all((opts.invalidate ?? []).map((queryKey) => qc.invalidateQueries({ queryKey }))),
  });
}
